import React, { useEffect } from "react";
import PropTypes from "prop-types";

import Button from "shared/Button";
import AddButton from "../AddButton";
import Checkbox from "shared/@forms/Checkbox";
// import Select from "../Select";
import { Select } from "shared/Select/Select";
import Input from "shared/@forms/Input";
import InputItem from "../InputItem";

import styles from "./TicketGroupItem.module.css";
import classNames from "classnames";

/**Displays the information about a ticket option. When edit is clicked it will open the edit ticket modal. */
export function TicketGroupItem({
  ticket,
  onEdit,
  isMajorV1,
  isV2,
  onAddGroup,
  onRemoveGroup,
  onAddLinkedTicket,
  onRemoveLinkedTicket,
  onRemoveCreditType,
  onCheckbox,
  onSelect,
  onCreditChange,
  onEditDates,
  isSub,
  onInputChange,
  onRemoveTicket,
  isPublished,
  pageMode,
  isVirtual,
  eventName,
  showLinkedVirtualTicketColumn,
}) {
  const getActiveArray = (field) => {
    // If saved is explicitly set (not null), use it even if empty
    if (field?.saved !== null && field?.saved !== undefined) {
      return Array.isArray(field.saved) ? field.saved : [];
    }

    // Fall back to value if saved is null
    if (Array.isArray(field?.value)) {
      return field.value;
    }

    return [];
  };

  const getActiveValue = (field) => {
    // If saved is explicitly set (not null), use it even if empty string
    if (field?.saved !== null && field?.saved !== undefined) {
      return field.saved;
    }

    // Fall back to value if saved is null
    return field?.value;
  };

  const ticketTypeCodeToNameMap = {
    null: "Select",
    FR_VR_LS: "Family Reunion - Live Stream Event",
    FR_VR_PR: "Family Reunion - Virtual Event",
    LS_STD: "Leadership Summit - Standard Ticket",
    FE_VR_LS: "Free Enterprise - Live Stream Event",
    FE_VR_PR: "Free Enterprise - Virtual Event",
    FR_STD: "Family Reunion - Standard Ticket",
    SL_STD: "Spring Leadership - Standard Ticket",
    SL_VR_LS: "Spring Leadership - Live Stream Event",
    SL_VR_PR: "Spring Leadership - Virtual Event",
    FE_STD: "Free Enterprise - Standard Ticket",
    LS_VR_LS: "Leadership Summit - Live Stream Event",
    LS_VR_PR: "Leadership Summit - Virtual Event",
    SC_STD: "Summer Conference - Standard Ticket",
    SC_VR_LS: "Summer Conference - Live Stream Event",
    ORG_STD: "Organizational Meeting - Standard Ticket",
    SC_STDPL: "Summer Conference - Standard Platinum Ticket",
    FE_STDPL: "Free Enterprise - Standard Platinum Ticket",
    SL_STDPL: "Spring Leadership - Standard Platinum Ticket",
    FR_STDPL: "Family Reunion - Standard Platinum Ticket",
    FE_COMP: "Free Enterprise - Comp Member Ticket",
    FR_COMP: "Family Reunion - Comp Member Ticket",
    LS_COMP: "Leadership Summit - Comp Member Ticket",
    SC_COMP: "Summer Conference - Comp Member Ticket",
    ORG_COMP: "Organizational Meeting - Comp Member Ticket",
    LS_FPL: "Leadership Summit - Founders Platinum Ticket",
    SL_FPL: "Spring Leadership - Founders Platinum Ticket",
    FR_FPL: "Family Reunion - Founders Platinum Ticket",
    FE_FPL: "Free Enterprise - Founders Platinum Ticket",
    SL_VIP: "Spring Leadership - VIP Ticket",
    LS_VIP: "Leadership Summit - VIP Ticket",
    FR_VIP: "Family Reunion - VIP Ticket",
    FE_VIP: "Free Enterprise - VIP Ticket",
    ORG_VIP: "Organizational Meeting - VIP Ticket",
    SC_VIP: "Summer Conference - VIP Ticket",
    FE_ADM: "Free Enterprise - Special Session Admission",
    FR_ADM: "Family Reunion - Special Session Admission",
    LS_ADM: "Leadership Summit - Special Session Admission",
    SL_ADM: "Spring Leadership - Special Session Admission",
    SC_ADM: "Summer Conference - Special Session Admission",
    FR_CANCEL: "Family Reunion - Cancel Fee",
    FE_CANCEL: "Free Enterprise - Cancel Fee",
    SL_CANCEL: "Spring Leadership - Cancel Fee",
    LS_CANCEL: "Leadership Summit - Cancel Fee",
    SC_CANCEL: "Summer Conference - Cancel Fee",
    //new
    LF_SEMINAR_COMP: "Local Function Seminar - Comp Member Ticket",
    LF_SEMINAR_PL: "Local Function - Seminar - Platinum Ticket",
    LF_SEMINAR_STD: "Local Function - Seminar - Standard Ticket",
    LF_SEMINAR_VIP: "Local Function - Seminar - VIP Ticket",
    LF_SLM_COMP: "Local Function - SLM - Comp Member Ticket",
    LF_SLM_PL: "Local Function - SLM - Platinum Ticket",
    LF_SLM_STD: "Local Function - SLM - Standard Ticket",
    LF_SLM_VIP: "Local Function - SLM - VIP Ticket",
    LF_VR_LS_COMP: "Local Function - Virtual - Comp Member Ticket",
    LF_VR_LS_STD: "Local Function Ticket - Live Stream Event",
    LF_VR_LS_VIP: "Local Function - Virtual - VIP Ticket",
  };
  const ticketTypeNameToCodeMap = {
    Select: null,
    "Family Reunion - Live Stream Event": "FR_VR_LS",
    "Family Reunion - Virtual Event": "FR_VR_PR",
    "Leadership Summit - Standard Ticket": "LS_STD",
    "Free Enterprise - Live Stream Event": "FE_VR_LS",
    "Free Enterprise - Virtual Event": "FE_VR_PR",
    "Family Reunion - Standard Ticket": "FR_STD",
    "Spring Leadership - Standard Ticket": "SL_STD",
    "Spring Leadership - Live Stream Event": "SL_VR_LS",
    "Spring Leadership - Virtual Event": "SL_VR_PR",
    "Free Enterprise - Standard Ticket": "FE_STD",
    "Leadership Summit - Live Stream Event": "LS_VR_LS",
    "Leadership Summit - Virtual Event": "LS_VR_PR",
    "Summer Conference - Standard Ticket": "SC_STD",
    "Organizational Meeting - Standard Ticket": "ORG_STD",
    "Summer Conference - Standard Platinum Ticket": "SC_STDPL",
    "Free Enterprise - Standard Platinum Ticket": "FE_STDPL",
    "Spring Leadership - Standard Platinum Ticket": "SL_STDPL",
    "Family Reunion - Standard Platinum Ticket": "FR_STDPL",
    "Free Enterprise - Comp Member Ticket": "FE_COMP",
    "Family Reunion - Comp Member Ticket": "FR_COMP",
    "Leadership Summit - Comp Member Ticket": "LS_COMP",
    "Summer Conference - Comp Member Ticket": "SC_COMP",
    "Organizational Meeting - Comp Member Ticket": "ORG_COMP",
    "Leadership Summit - Founders Platinum Ticket": "LS_FPL",
    "Spring Leadership - Founders Platinum Ticket": "SL_FPL",
    "Family Reunion - Founders Platinum Ticket": "FR_FPL",
    "Free Enterprise - Founders Platinum Ticket": "FE_FPL",
    "Spring Leadership - VIP Ticket": "SL_VIP",
    "Leadership Summit - VIP Ticket": "LS_VIP",
    "Family Reunion - VIP Ticket": "FR_VIP",
    "Free Enterprise - VIP Ticket": "FE_VIP",
    "Organizational Meeting - VIP Ticket": "ORG_VIP",
    "Summer Conference - VIP Ticket": "SC_VIP",
    "Free Enterprise - Special Session Admission": "FE_ADM",
    "Family Reunion - Special Session Admission": "FR_ADM",
    "Leadership Summit - Special Session Admission": "LS_ADM",
    "Spring Leadership - Special Session Admission": "SL_ADM",
    "Summer Conference - Special Session Admission": "SC_ADM",
    "Family Reunion - Cancel Fee": "FR_CANCEL",
    "Free Enterprise - Cancel Fee": "FE_CANCEL",
    "Spring Leadership - Cancel Fee": "SL_CANCEL",
    "Leadership Summit - Cancel Fee": "LS_CANCEL",
    "Summer Conference - Cancel Fee": "SC_CANCEL",
    //new
    "Local Function Seminar - Comp Member Ticket": "LF_SEMINAR_COMP",
    "Local Function - Seminar - Platinum Ticket": "LF_SEMINAR_PL",
    "Local Function - Seminar - Standard Ticket": "LF_SEMINAR_STD",
    "Local Function - Seminar - VIP Ticket": "LF_SEMINAR_VIP",
    "Local Function - SLM - Comp Member Ticket": "LF_SLM_COMP",
    "Local Function - SLM - Platinum Ticket": "LF_SLM_PL",
    "Local Function - SLM - Standard Ticket": "LF_SLM_STD",
    "Local Function - SLM - VIP Ticket": "LF_SLM_VIP",
    "Local Function - Virtual - Comp Member Ticket": "LF_VR_LS_COMP",
    "Local Function - Virtual - Live Stream Event": "LF_VR_LS_STD",
    "Local Function - Virtual - VIP Ticket": "LF_VR_LS_VIP",
  };
  const isLocalFunctionEvent =
    eventName === "Second Look Meeting" || eventName === "Regional";
  const isFamilyReunion = eventName.toLowerCase().includes("family reunion");
  const baseName = isLocalFunctionEvent
    ? "Local Function"
    : isFamilyReunion
    ? "Family Reunion"
    : eventName;
  const filteredTicketOptions = [
    "Select",
    "Spring Leadership - Standard Ticket",
    "Spring Leadership - Standard Platinum Ticket",
    "Spring Leadership - Cancel Fee",
    "Spring Leadership - Live Stream Event",
    "Spring Leadership - Virtual Event",
    "Spring Leadership - Special Session Admission",
    "Spring Leadership - Founders Platinum Ticket",
    "Spring Leadership - VIP Ticket",
    "Free Enterprise - Standard Ticket",
    "Free Enterprise - Standard Platinum Ticket",
    "Free Enterprise - Cancel Fee",
    "Free Enterprise - Live Stream Event",
    "Free Enterprise - Virtual Event",
    "Free Enterprise - Special Session Admission",
    "Free Enterprise - VIP Ticket",
    "Family Reunion - Standard Ticket",
    "Family Reunion - Standard Platinum Ticket",
    "Family Reunion - Founders Platinum Ticket",
    "Family Reunion - Comp Member Ticket",
    "Family Reunion - Cancel Fee",
    "Family Reunion - Live Stream Event",
    "Family Reunion - Virtual Event",
    "Family Reunion - Special Session Admission",
    "Family Reunion - VIP Ticket",
    "Leadership Summit - Standard Ticket",
    "Leadership Summit - Founders Platinum Ticket",
    "Leadership Summit - Comp Member Ticket",
    "Leadership Summit - Cancel Fee",
    "Leadership Summit - Live Stream Event",
    "Leadership Summit - Virtual Event",
    "Leadership Summit - Special Session Admission",
    "Leadership Summit - VIP Ticket",
    "Summer Conference - Special Session Admission",
    "Summer Conference - Comp Member Ticket",
    "Summer Conference - Standard Ticket",
    "Summer Conference - Standard Platinum Ticket",
    "Summer Conference - VIP Ticket",
    "Summer Conference - Cancel Fee",
    //new
    "Local Function Seminar - Comp Member Ticket",
    "Local Function - Seminar - Platinum Ticket",
    "Local Function - Seminar - Standard Ticket",
    "Local Function - Seminar - VIP Ticket",
    "Local Function - SLM - Comp Member Ticket",
    "Local Function - SLM - Platinum Ticket",
    "Local Function - SLM - Standard Ticket",
    "Local Function - SLM - VIP Ticket",
    "Local Function - Virtual - Comp Member Ticket",
    "Local Function - Virtual - Live Stream Event",
    "Local Function - Virtual - VIP Ticket",
  ].filter((option) => {
    return option === "Select" || option.startsWith(baseName);
  });

  const isReadOnlyPage = pageMode === "view";
  const canEditRepublishField = (permission) =>
    permission === "write" && !isReadOnlyPage;
  const canEditGroups = canEditRepublishField(
    ticket.limitedToGroups?.permission
  );
  const canEditDates =
    !isReadOnlyPage &&
    [
      ticket?.purchaseStartDate?.permission,
      ticket?.purchaseEndDate?.permission,
      ticket?.startFeeDate?.permission,
      ticket?.changeableUntilDate?.permission,
    ].includes("write");

  return isMajorV1 ? (
    <div className={styles.ticketGroupItemContainerV1}>
      <span>{ticket.ticketId}</span>
      <span>{ticket.name}</span>
      <span className={styles.price}>{` $${ticket.price} USD`}</span>{" "}
      <span className={styles.price}>{ticket.cancelPrice}</span>
      {/* <Button
        size="small"
        className={styles.editButton}
        onClick={() => onEdit(ticket)}
      >
        Edit
      </Button> */}
    </div>
  ) : isV2 ? (
    <div
      className={
        isVirtual || isSub || !showLinkedVirtualTicketColumn
          ? styles.ticketGroupItemContainerV2
          : styles.ticketGroupItemContainerV2Virtual
      }
    >
      <span>
        {ticket.name?.permission == "write" && !isPublished ? (
          <div className={styles.subItemName}>
            <Input
              value={ticket.name?.saved ?? ticket.name?.value ?? ""}
              className={styles.input}
              onChange={(e) => onInputChange(e.target.value, "name")}
            />
          </div>
        ) : ticket.name?.permission !== "hidden" ? (
          ticket.name?.saved ?? ticket.name?.value
        ) : (
          <></>
        )}
      </span>
      <span>
        {ticket.price?.permission == "write" && !isPublished ? (
          <div className={styles.subItem}>
            <Input
              type="number"
              value={
                (ticket.price?.saved ?? ticket.price?.value) == null
                  ? ""
                  : ticket.price?.saved ?? ticket.price?.value
              }
              className={styles.input}
              onChange={(e) => onInputChange(e.target.value, "price")}
            />
          </div>
        ) : ticket.price?.permission !== "hidden" ? (
          ` $${ticket.price?.saved ?? ticket.price?.value}`
        ) : (
          <></>
        )}
      </span>
      <span className={styles.ticketIdCell}>
        {ticket.commonItemId?.permission == "write" && !isPublished ? (
          <div className={styles.selectContainer}>
            <Select
              options={filteredTicketOptions}
              placeholder={
                ticket?.commonItemId?.saved ??
                ticket?.commonItemId?.value ??
                "Select"
              }
              onChange={(newValue) =>
                onSelect(
                  ticketTypeNameToCodeMap[newValue],
                  ticket.id,
                  "commonItemId"
                )
              }
              removeSelectedOption={false}
              highlightOption={
                ticketTypeCodeToNameMap[
                  ticket?.commonItemId?.saved ?? ticket?.commonItemId?.value
                ]
              }
            />
          </div>
        ) : ticket.commonItemId?.permission !== "hidden" ? (
          <div className={styles.ticketIdValue}>
            {ticket?.commonItemId.saved ?? ticket?.commonItemId.value}
          </div>
        ) : (
          <></>
        )}
      </span>

      {ticket.cancelFee?.permission == "write" && !isPublished ? (
        <span>
          <div className={styles.subItem}>
            <Input
              type="number"
              value={
                (ticket.cancelFee?.saved ?? ticket.cancelFee?.value) == null
                  ? ""
                  : ticket.cancelFee?.saved ?? ticket.cancelFee?.value
              }
              className={styles.input}
              onChange={(e) => onInputChange(e.target.value, "cancelFee")}
            />
          </div>
        </span>
      ) : ticket.cancelFee?.permission !== "hidden" ? (
        <span>${ticket.cancelFee.saved ?? ticket.cancelFee.value}</span>
      ) : (
        <></>
      )}
      <span>
        {ticket.cancelFeeItemId?.permission == "write" && !isPublished ? (
          <div className={styles.selectContainer}>
            <Select
              options={filteredTicketOptions}
              placeholder={
                ticket?.cancelFeeItemId?.saved ??
                ticket?.cancelFeeItemId?.value ??
                "Select"
              }
              onChange={(newValue) =>
                onSelect(
                  ticketTypeNameToCodeMap[newValue],
                  ticket.id,
                  "cancelFeeItemId"
                )
              }
              removeSelectedOption={false}
              highlightOption={
                ticketTypeCodeToNameMap[
                  ticket?.cancelFeeItemId?.saved ??
                    ticket?.cancelFeeItemId?.value
                ]
              }
            />
          </div>
        ) : ticket.cancelFeeItemId?.permission !== "hidden" ? (
          ticket?.cancelFeeItemId.saved ?? ticket?.cancelFeeItemId.value
        ) : (
          <></>
        )}
      </span>

      {!isVirtual && !isSub && showLinkedVirtualTicketColumn && (
        <span>
          {ticket.linkedVirtualTicket?.permission == "write" && !isPublished ? (
            <div className={styles.linkedTicketContainer}>
              {!(
                ticket?.linkedVirtualTicket?.saved?.ticketId ??
                ticket?.linkedVirtualTicket?.value?.ticketId
              ) && (
                <div className={styles.addButtonWrapper}>
                  <div
                    className={styles.addGroupsButton}
                    onClick={() =>
                      onAddLinkedTicket(
                        ticket.id,
                        ticket?.linkedVirtualTicket?.saved?.ticketId ??
                          ticket?.linkedVirtualTicket?.value?.ticketId,
                        ticket?.linkedVirtualTicket?.saved?.name ??
                          ticket?.linkedVirtualTicket?.value?.name
                      )
                    }
                  >
                    <AddButton description={"Add Ticket"} color="#2A3847" />
                  </div>
                </div>
              )}
              <div className={styles.groupsGrid}>
                {(ticket?.linkedVirtualTicket?.saved?.ticketId ??
                  ticket?.linkedVirtualTicket?.value?.ticketId) && (
                  <div className={styles.groupItem}>
                    {(ticket?.linkedVirtualTicket?.saved?.name ??
                      ticket?.linkedVirtualTicket?.value?.name) ||
                      "No Name For Ticket"}

                    {!isPublished && (
                      <div
                        className={styles.removeGroup}
                        onClick={() => onRemoveLinkedTicket(ticket.id)}
                      >
                        &times;
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : ticket.linkedVirtualTicket?.permission !== "hidden" ? (
            <div className={styles.groupsGrid}>
              {(ticket?.linkedVirtualTicket?.saved?.ticketId ??
                ticket?.linkedVirtualTicket?.value?.ticketId) && (
                <div className={styles.groupItem}>
                  {(ticket?.linkedVirtualTicket?.saved?.name ??
                    ticket?.linkedVirtualTicket?.value?.name) ||
                    "No Name For Ticket"}
                </div>
              )}
            </div>
          ) : (
            <></>
          )}
        </span>
      )}

      <span className={styles.creditTypesContainer}>
        {ticket.acceptedCreditTypes?.permission == "write" ? (
          <>
            <div className={styles.editCreditTypesButtonContainer}>
              <Button
                className={styles.editCreditTypesButton}
                children={!isPublished ? "Edit" : "View"}
                onClick={() => onCreditChange(ticket.id)}
              />
            </div>
            {/* <div className={styles.creditTypesList}>
          {(ticket.acceptedCreditTypes.saved
            ? ticket.acceptedCreditTypes.saved
            : ticket.acceptedCreditTypes?.value || []
          )?.map((type) => (
            <span key={type.id} className={styles.creditTypesItem}>
              {type.creditTypeId}
              <div
                className={styles.removeCreditType}
                onClick={() => onRemoveCreditType(type, ticket.id)}
              >
                &times;
              </div>
            </span>
          ))}
        </div> */}
          </>
        ) : ticket.acceptedCreditTypes?.permission !== "hidden" ? (
          <div className={styles.creditTypesList}>
            {(ticket.acceptedCreditTypes.saved
              ? ticket.acceptedCreditTypes.saved
              : ticket.acceptedCreditTypes?.value || []
            )?.map((type) => (
              <span key={type.id} className={styles.creditTypesItem}>
                {type.creditTypeId}
                {/* <div
                className={styles.removeCreditType}
                onClick={() => onRemoveCreditType(type, ticket.id)}
              >
                &times;
              </div> */}
              </span>
            ))}
          </div>
        ) : (
          <></>
        )}
      </span>

      <span className={styles.groups}>
        {canEditGroups ? (
          <div className={styles.groupsContainer}>
            <div className={styles.addButtonWrapper}>
              <div
                className={styles.addGroupsButton}
                onClick={() => onAddGroup(ticket.id)}
              >
                <AddButton description={"Add Groups"} color="#2A3847" />
              </div>
            </div>
            <div className={styles.groupsGrid}>
              {getActiveArray(ticket?.limitedToGroups).map((group) => (
                <div
                  className={styles.groupItem}
                  key={group?.id || group?.name}
                >
                  {group?.name || "No Name For Group"}
                  {canEditGroups && (
                    <div
                      className={styles.removeGroup}
                      onClick={() => onRemoveGroup(group, ticket.id)}
                    >
                      &times;
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : ticket.limitedToGroups?.permission !== "hidden" ? (
          <div className={styles.groupsContainerReadOnly}>
            <div className={styles.groupsGridReadOnly}>
              {getActiveArray(ticket?.limitedToGroups).map((group) => (
                <div
                  className={styles.groupItem}
                  key={group?.id || group?.groupId || group?.name}
                >
                  {group?.name || "No Name For Group"}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
      </span>

      <span className={styles.checkbox}>
        <div className={styles.editCreditTypesButtonContainer}>
          <Button
            className={styles.editCreditTypesButton}
            children={canEditDates ? "Edit" : "View"}
            onClick={() => onEditDates(ticket.id)}
          />
        </div>
      </span>

      <span className={styles.checkbox}>
        <div className={styles.editCreditTypesButtonContainer}>
          {!isPublished && (
            <Button
              className={styles.removeButton}
              children={"Remove"}
              onClick={() => onRemoveTicket(ticket.id)}
            />
          )}
        </div>
      </span>
    </div>
  ) : (
    <div className={styles.ticketGroupItemContainer}>
      <span>{ticket.id}</span>
      <span>{ticket.name}</span>
      <span className={styles.price}>{` $${ticket.price} USD`}</span>{" "}
      <span>{`${ticket.startDate} ${ticket.startTime}`}</span>
      <span>{`${ticket.endDate} ${ticket.endTime}`}</span>
      <span className={styles.timezone}>{ticket.timeZone}</span>
      <Button
        size="small"
        className={styles.editButton}
        onClick={() => onEdit(ticket)}
      >
        Edit
      </Button>
    </div>
  );
}

TicketGroupItem.propTypes = {
  /**Object with the tickets info (id-String, name-String, price-Number, timeZone-String, startDate-String, endDate-String, startTime-String, endTime-String)  */
  ticket: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
    timeZone: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
  }),
  /**Function called to edit the ticket information. Opens the edit ticket modal */
  onEdit: PropTypes.func,
  isMajorV1: PropTypes.bool,
  pageMode: PropTypes.string,
};
